// ReservationPage.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from "react-router-dom";
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import WeeklyCalendar from "./WeeklyCalendar";
import MapPickerGoogle from "./MapPickerGoogle";
import AlertDialog from "../../Lms/Components/Shared/AlertDialog";
import ConfirmDialog from "../../Lms/Components/Shared/ConfirmDialog";
import { ENDPOINTS } from "./../Utils/Endpoints";
import { Models } from "./../Utils/ModelsCarBooking";
import axios from "axios";
import './../../../style/ElementsStyle.css';
import './../../../style/ContainersStyles.css';
import "../Styles/Drivers.css";
import './../../../style/ButtonOverride.css';

import {
    getSessionUserId,
    getSessionUserName,
} from "../../../utils/SessionUser";

// Utility functions
import {
    today,
    weekdayLongES,
    startOfDay,
    getMonday,
    formatDateLocal,
    getISOWeek,
    isWeekend,
    toDateWithTime,
    normalizeDateOnly
} from '../Utils/dateHelpers';

import {
    toMinutes,
    isInsideInterval,
    buildDailyIntervalsForDriver
} from '../Utils/timeHelpers';

import {
    ESTADO_INFO,
    TIPO_APROBACION,
    getEstadoInfo,
    getDriverColorFromId
} from '../Utils/reservationConstants';

import {
    parseJsonLocation,
    mapReservaToEvent
} from '../Utils/reservationMappers';

const enpointReservas = ENDPOINTS.reserva;
const modeloReservas = Models.reserva;

const endpointChoferes = ENDPOINTS.chofer;
const modeloChofer = Models.chofer;

const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointCorreo = ENDPOINTS.correos;
const modeloCorreo = Models.correos;

const endpointTurnos = ENDPOINTS.turnos;
const modeloTurnos = Models.turnos;

const endpointEmpleadoTurno = ENDPOINTS.empleadosTurnos;
const modeloEmpleadoTurnos = Models.empleadosTurnos;

// 🔹 Relación Empleado ↔ Unidad Organizacional (UnidadesOrgEmpleado)
const endpointUnidadesOrgEmpleado = ENDPOINTS.unidadesOrgEmpleado;
const modeloUnidadesOrgEmpleado = Models.unidadOrgEmpleado;

// 🔹 Relación Solicitante ↔ Unidades Organizacionales
const endpointSolicitanteUnidadesOrg =
    ENDPOINTS.solicitanteUnidadesOrganizacionales;
const modeloSolicitanteUnidadesOrg =
    Models.solicitanteUnidadesOrganizacionales;

// 🔹 Extras (semana / mes)
const endpointExtrasMes = ENDPOINTS.extrasAcumuladoMes;
const modeloExtrasMes = Models.extrasAcumuladoMes;

const endpointExtrasSemana = ENDPOINTS.extrasAcumuladoSemana;
const modeloExtrasSemana = Models.extrasAcumuladoSemana;

// 🆕 Parámetros laborales (límites de horas extra por UO)
const endpointParametrosLaborales = ENDPOINTS.parametrosLaborales;
const modeloParametrosLaborales = Models.parametrosLaborales;

// 🔹 Solicitantes + Usuarios
const endpointSolicitantes = ENDPOINTS.solicitantes;
const modeloSolicitante = Models.solicitantes;

const endpointUsuarios = ENDPOINTS.usuarios;

// 🔹 Excepciones de turno
const endpointExcepcionTurno = ENDPOINTS.excepcionTurno;
const modeloExcepcionTurno = Models.excepcionTurno;

// 🆕 Días festivos inhábiles
const endpointDiasFestivos = ENDPOINTS.diasFestivosInhabiles;
const modeloDiasFestivos = Models.diasFestivosInhabilies;

function formatReservationRange(startDateStr, startTimeStr, endDateStr, endTimeStr) {
    if (!startDateStr || !startTimeStr || !endDateStr || !endTimeStr) {
        return "";
    }

    const start = toDateWithTime(startDateStr, startTimeStr);
    const end = toDateWithTime(endDateStr, endTimeStr);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return "";
    }

    const sameDate = start.toDateString() === end.toDateString();
    const startDate = start.toLocaleDateString("es-MX");
    const endDate = end.toLocaleDateString("es-MX");
    const startTime = start.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
    const endTime = end.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

    if (sameDate) {
        return `${startDate} · ${startTime} - ${endTime}`;
    }

    return `${startDate} · ${startTime} → ${endDate} · ${endTime}`;
}

// ===== Funciones helper movidas a Utils =====
// - Funciones de fechas → dateHelpers.js
// - Funciones de tiempo → timeHelpers.js  
// - Constantes → reservationConstants.js
// - Mappers → reservationMappers.js

export default function ReservationPage() {
    const navigate = useNavigate();

    // 🔹 Usuario en sesión
    const [sessionUserId] = useState(() => getSessionUserId());
    const [sessionUserName] = useState(() => getSessionUserName());

    // 🔒 Errores críticos de sesión (bloquean el módulo)
    const [sessionErrors, setSessionErrors] = useState([]);
    const hasSessionErrors = sessionErrors.length > 0;

    const [events, setEvents] = useState([]);
    const [anchor, setAnchor] = useState(new Date());
    const [editingId, setEditingId] = useState(null);

    const [choferes, setChoferes] = useState([]);
    const [reservas, setReservas] = useState([]); // fuente de verdad reservas
    const [turnos, setTurnos] = useState([]); // turnos desde BD
    const [empleadosTurnos, setEmpleadosTurnos] = useState([]); // tabla intermedia
    const [excepcionesTurno, setExcepcionesTurno] = useState([]); // excepciones de turno

    // 🔹 Empleado ↔ Unidad Organizacional (GLOBAL para filtrado)
    const [allEmpleadosUnidadOrg, setAllEmpleadosUnidadOrg] = useState([]);
    // 🔹 Empleado ↔ Unidad Organizacional (ESPECÍFICO del chofer seleccionado)
    const [driverUnidadesOrg, setDriverUnidadesOrg] = useState([]);

    // 🔹 Solicitante ↔ UO (relación)
    const [relSolicitanteUO, setRelSolicitanteUO] = useState([]);
    // 🔹 UO permitidas para el solicitante en sesión
    const [unidadesOrgPermitidasIds, setUnidadesOrgPermitidasIds] = useState([]);

    // 🔹 extras semana/mes
    const [extrasMes, setExtrasMes] = useState([]);
    const [extrasSemana, setExtrasSemana] = useState([]);

    // 🆕 Parámetros laborales
    const [parametrosLaborales, setParametrosLaborales] = useState([]);

    // 🆕 Días festivos desde BD
    const [diasFestivos, setDiasFestivos] = useState([]);

    const [showSchedule, setShowSchedule] = useState(false);
    const [savedStatus, setSavedStatus] = useState(null);
    const [savedEstadoId, setSavedEstadoId] = useState(null);
    const [hasRhDecision, setHasRhDecision] = useState(false);
    const [originalRes, setOriginalRes] = useState(null);
    const [showRejected, setShowRejected] = useState(false);
    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showEventPopup, setShowEventPopup] = useState(false);
    const [showUserInfoModal, setShowUserInfoModal] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: 768 });


    const [origin, setOrigin] = useState({
        address: "",
        location: null,
    });
    const [destination, setDestination] = useState({
        address: "",
        location: null,
    });
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerTarget, setPickerTarget] = useState("origin");

    const [form, setForm] = useState({
        idSolicitante: 0,
        idChofer: 0,
        fecha: formatDateLocal(new Date()),
        fechaFin: formatDateLocal(new Date()),
        horaInicio: "",
        horaFin: "",
        origenTexto: "",
        destinoTexto: "",
        motivo: "",
        observaciones: "",
        chofer: "",
        emailChofer: "",
        email: "",
        nombre: "",
    });

    // 🔹 idSolicitante de la sesión y flag de edición
    const [sessionSolicitanteId, setSessionSolicitanteId] = useState(null);
    const [canEdit, setCanEdit] = useState(true);

    const driverColor = useMemo(
        () => getDriverColorFromId(form.idChofer || 0),
        [form.idChofer]
    );

    const isReadOnly = Boolean(editingId && !canEdit);

    // 🔹 ¿El horario seleccionado choca con otra reserva del mismo chofer?
    const hasOverlap = useMemo(() => {
        // si falta info básica, no validamos nada todavía
        if (!form.idChofer || !form.fecha || !form.horaInicio || !form.horaFin) {
            return false;
        }

        const start = toDateWithTime(form.fecha, form.horaInicio);
        let end = toDateWithTime(form.fecha, form.horaFin);

        // Soporte para reservas nocturnas (terminan al día siguiente)
        if (end <= start) {
            end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        }

        const allEvents = (reservas || []).map((r) => mapReservaToEvent(r));

        return allEvents.some((ev) => {
            const evChoferId = ev.meta?.idChofer ?? null;
            const evEstadoId = ev.meta?.estadoId ?? 1;

            // mismo chofer
            if (!evChoferId || String(evChoferId) !== String(form.idChofer)) {
                return false;
            }

            // misma fecha
            const evDateStr = formatDateLocal(ev.start);
            if (evDateStr !== form.fecha) return false;

            // ignorar la reserva que estoy editando
            if (editingId && ev.id === editingId) return false;

            // ignorar canceladas (5 = CANCELLED)
            if (evEstadoId === 5) return false;

            // ¿Se cruzan [start, end) con [ev.start, ev.end)?
            return start < ev.end && end > ev.start;
        });
    }, [
        reservas,
        form.idChofer,
        form.fecha,
        form.horaInicio,
        form.horaFin,
        editingId,
    ]);

    function filterEventsForView(
        allEvents,
        showRejectedFlag,
        showOnlyMineFlag,
        sessionSolId,
        choferId
    ) {
        // Si no tenemos solicitante ligado a la sesión, por seguridad NO mostramos nada
        if (!sessionSolId) {
            return [];
        }

        const filtered = allEvents.filter((e) => {
            const idEstado = e.meta?.estadoId ?? null;
            if (!idEstado) return false;

            const idSolEvt = e.meta?.idSolicitante ?? null;
            const isMine = idSolEvt != null && String(idSolEvt) === String(sessionSolId);

            // 🔹 Estados activos: 1 PENDING, 2 CONFIRMED, 3 AUTHORIZED
            if ([1, 2, 3].includes(idEstado)) {
                // Si showOnlyMine está activo, solo mostrar las mías
                if (showOnlyMineFlag) {
                    return isMine;
                }
                // Si showOnlyMine está desactivado, mostrar de TODOS los usuarios
                return true;
            }

            // 🔹 Estados rechazados/terminados: 4 REJECTED, 5 CANCELLED, 6 COMPLETED
            // SIEMPRE filtrar por usuario actual (solo mostrar las mías)
            if ([4, 5, 6].includes(idEstado)) {
                // Solo mostrar si el toggle está activo Y es mía
                return showRejectedFlag && isMine;
            }

            return false;
        });

        // Si además hay chofer seleccionado, filtramos por ese chofer
        if (choferId) {
            return filtered.filter((e) => {
                const idChoferEvt = e.meta?.idChofer ?? null;
                return idChoferEvt != null && String(idChoferEvt) === String(choferId);
            });
        }

        return filtered;
    }

    const loadDataReservas = async (currentAnchor) => {
        try {
            // 🚀 OPTIMIZACIÓN: Solo cargar reservas cercanas al anchor del calendario (+/- 30 días)
            const baseDate = currentAnchor || anchor || new Date();
            const start = new Date(baseDate);
            start.setDate(start.getDate() - 30);
            const end = new Date(baseDate);
            end.setDate(end.getDate() + 30);

            // Usamos formato YYYY-MM-DD para evitar problemas con zonas horarias o caracteres especiales
            const sStr = start.getFullYear() + "-" + String(start.getMonth() + 1).padStart(2, '0') + "-" + String(start.getDate()).padStart(2, '0');
            const eStr = end.getFullYear() + "-" + String(end.getMonth() + 1).padStart(2, '0') + "-" + String(end.getDate()).padStart(2, '0');

            console.log(`[ReservationPage] Cargando reservas desde ${sStr} hasta ${eStr}`);
            const res = await axios.get(ENDPOINTS.reservaRange(sStr, eStr));
            setReservas((res.data || []).map(modeloReservas));
        } catch (err) {
            console.error("[ReservationPage] Error cargando reservas por rango:", err);
        }
    };

    const loadDataChoferes = async () => {
        try {
            // 🚀 ULTRA-OPTIMIZACIÓN: Usamos un único endpoint que ya trae los datos enriquecidos (Nombre, Correo, etc.)
            // Esto evita descargar miles de registros de Personas/Empleados y realizar joins manuales en JS.
            const res = await axios.get(ENDPOINTS.choferDetalle);
            const enrichedChoferes = (res.data || []).map(modeloChofer);

            console.log("[ReservationPage] Choferes cargados (Enriched):", enrichedChoferes.length);
            setChoferes(enrichedChoferes);

        } catch (err) {
            console.error(err);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al cargar la lista de choferes",
                variant: "error"
            });
        }
    };

    const loadDriverAvailability = async (idChofer) => {
        if (!idChofer) {
            setExcepcionesTurno([]);
            setEmpleadosTurnos([]);
            setTurnos([]);
            setExtrasMes([]);
            setExtrasSemana([]);
            setDriverUnidadesOrg([]);
            return;
        }
        try {
            const res = await axios.get(ENDPOINTS.choferDisponibilidad(idChofer));
            const data = res.data || {};

            console.log("[DEBUG] Disponibilidad Recibida:", {
                idChofer,
                data
            });

            setExcepcionesTurno((data.excepciones || data.Excepciones || []).map(modeloExcepcionTurno));
            setEmpleadosTurnos((data.asignaciones || data.Asignaciones || []).map(modeloEmpleadoTurnos));
            setTurnos((data.turnos || data.Turnos || []).map(modeloTurnos));
            setExtrasMes((data.extrasMes || data.ExtrasMes || []).map(modeloExtrasMes));
            setExtrasSemana((data.extrasSemana || data.ExtrasSemana || []).map(modeloExtrasSemana));
            setDriverUnidadesOrg((data.unidadesOrg || data.UnidadesOrg || []).map(modeloUnidadesOrgEmpleado));
        } catch (err) {
            console.error("[ReservationPage] Error cargando disponibilidad del chofer:", err);
        }
    };


    // 🆕 Cargar relación Solicitante ↔ Unidades Organizacionales (específico)
    const loadDataSolicitanteUnidadesOrg = async (idSolicitante) => {
        if (!idSolicitante) return;
        try {
            // 🚀 Usamos directamente el endpoint de filtro por Solicitante para evitar el 404 previo
            const resRel = await axios.get(`${ENDPOINTS.solicitanteUnidadesOrganizacionales}/Solicitante/${idSolicitante}`);

            setRelSolicitanteUO(
                (resRel.data || []).map(modeloSolicitanteUnidadesOrg)
            );
        } catch (err) {
            console.error("[ReservationPage] Error cargando UOs del solicitante:", err);
        }
    };

    // 🆕 Cargar TODAS las relaciones Empleado-UO para filtrar dropdown de choferes
    // Esto es necesario para que el filtro 'choferesFiltrados' funcione antes de seleccionar un chofer.
    const loadDataUnidadesOrgEmpleadoAll = async () => {
        try {
            const res = await axios.get(ENDPOINTS.unidadesOrgEmpleado);
            setAllEmpleadosUnidadOrg((res.data || []).map(modeloUnidadesOrgEmpleado));
        } catch (err) {
            console.error(err);
        }
    };

    // 🆕 Cargar parámetros laborales
    const loadDataParametrosLaborales = async () => {
        try {
            const res = await axios.get(endpointParametrosLaborales);
            setParametrosLaborales(
                (res.data || []).map(modeloParametrosLaborales)
            );
        } catch (err) {
            console.error(err);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al cargar los parámetros laborales",
                variant: "error"
            });
        }
    };

    // 🆕 Cargar días festivos inhábiles
    const loadDataDiasFestivos = async () => {
        try {
            const res = await axios.get(endpointDiasFestivos);
            setDiasFestivos((res.data || []).map(modeloDiasFestivos));
        } catch (err) {
            console.error(err);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al cargar los días festivos inhábiles",
                variant: "error"
            });
        }
    };

    // Carga inicial desde el backend
    useEffect(() => {
        loadDataReservas(anchor);
        loadDataChoferes();
        loadDataUnidadesOrgEmpleadoAll(); // Restaurado para filtro de choferes
        loadDataParametrosLaborales();
        loadDataDiasFestivos();
    }, []);

    // 🔄 Recargar reservas cuando cambia el anchor del calendario
    useEffect(() => {
        loadDataReservas(anchor);
    }, [anchor]);

    // 🔹 Vincular idUsuario (sesión) → Usuarios → Persona → Empleado → Solicitante → Email
    useEffect(() => {
        let cancel = false;

        async function loadSolicitanteFromSession() {
            const errors = [];
            let idPersona = null;
            let solSesion = null;
            let personaSesion = null;
            let correoSesion = null;

            try {
                if (!sessionUserId) {
                    const msg =
                        "[ReservationPage] No hay idUsuario en sesión; no se puede operar el módulo de reservas.";
                    console.warn(msg);
                    errors.push(
                        "No se detecta un usuario en sesión. No es posible registrar reservas."
                    );
                } else {
                    // 1) Datos de Usuario (específico)
                    const resUser = await axios.get(ENDPOINTS.usuariosById(sessionUserId));
                    const rawUser = resUser.data;

                    if (!rawUser) {
                        console.warn("[ReservationPage] El usuario de sesión no existe.");
                        errors.push("El usuario de sesión no está registrado en el catálogo de usuarios.");
                    } else {
                        // Extraer IdPersona con soporte para PascalCase/camelCase y asegurando que sea un ID válido (>0)
                        idPersona = rawUser.IdPersona ?? rawUser.idPersona ?? rawUser['IdPersona'] ?? rawUser['idPersona'] ?? null;

                        if (!idPersona || idPersona === 0) {
                            console.warn("[ReservationPage] El usuario de sesión no tiene Persona asociada (idPersona es 0 o null).", rawUser);
                            errors.push("El usuario de sesión no está vinculado a una Persona válida en el sistema.");
                        } else {
                            // 2) Empleado, Solicitante, Persona y Email se pueden buscar más específicamente
                            // Buscamos empleado por persona (si tuviéramos GetByPersona, pero usemos catálogos si no hay de otra,
                            // o busquemos en el catálogo de Empleados filtrando)

                            // Nota: Como no hay GetEmpleadoByPersona, cargamos catálogo Empleados (pero al menos lo demás es específico)
                            const [resEmp, resPer, resMail] = await Promise.all([
                                axios.get(ENDPOINTS.empleados),
                                axios.get(ENDPOINTS.personasById(idPersona)),
                                axios.get(ENDPOINTS.emailsByPersona(idPersona))
                            ]);

                            const empleados = (resEmp.data || []).map(modeloEmpleado);
                            const empSesion = empleados.find(e => e.idPersona === idPersona);

                            if (!empSesion) {
                                errors.push("El usuario de sesión no está registrado como Empleado.");
                            } else {
                                // Solicitante específico por empleado
                                const resSol = await axios.get(ENDPOINTS.solicitantesByEmpleado(empSesion.idEmpleado));
                                // El endpoint GetByEmpleado retorna una lista
                                solSesion = (resSol.data || []).map(modeloSolicitante)[0];

                                if (!solSesion) {
                                    errors.push("El usuario de sesión no está registrado como Solicitante de viajes.");
                                }
                            }

                            personaSesion = modeloPersona(resPer.data);
                            const correos = (resMail.data || []).map(modeloCorreo);
                            correoSesion = correos.find(c => c.esPrincipal === true) || correos[0];

                            if (!correoSesion) {
                                errors.push("El usuario de sesión no tiene un correo electrónico registrado.");
                            }
                        }
                    }
                }

                if (cancel) return;

                const nombreCompleto = personaSesion
                    ? `${personaSesion.nombre || ""} ${personaSesion.apellidoPaterno || ""
                        } ${personaSesion.apellidoMaterno || ""}`.trim()
                    : sessionUserName || "";

                const correoUsuario = correoSesion?.correo || "";
                const idSolSesion = solSesion?.idSolicitante || null;

                // Actualizamos nombre/correo en el formulario aunque haya errores
                setForm((f) => ({
                    ...f,
                    nombre: nombreCompleto || f.nombre,
                    email: correoUsuario || f.email,
                    idSolicitante: idSolSesion || f.idSolicitante,
                }));

                if (errors.length > 0) {
                    setSessionErrors(errors);
                    setSessionSolicitanteId(null);
                } else {
                    setSessionErrors([]);
                    setSessionSolicitanteId(idSolSesion);
                    // 🚀 Cargar UOs permitidas para este solicitante específico
                    if (idSolSesion) {
                        loadDataSolicitanteUnidadesOrg(idSolSesion);
                    }
                }
            } catch (err) {
                console.error(
                    "[ReservationPage] Error ligando usuario de sesión a solicitante:",
                    err
                );
                if (cancel) return;
                const msg =
                    "Ocurrió un error al intentar resolver la información del solicitante para la sesión actual.";
                setSessionErrors([msg]);
                setSessionSolicitanteId(null);
                // alert(msg);
            }
        }

        loadSolicitanteFromSession();

        return () => {
            cancel = true;
        };
    }, [sessionUserId, sessionUserName]);

    // 🆕 Calcular UO permitidas para el solicitante en sesión
    useEffect(() => {
        if (!sessionSolicitanteId) {
            setUnidadesOrgPermitidasIds([]);
            return;
        }

        const ids = [
            ...new Set(
                (relSolicitanteUO || [])
                    .filter((r) => {
                        const idSol =
                            r.idSolicitante ??
                            r.IdSolicitante ??
                            r["idSolicitante"] ??
                            r["IdSolicitante"];
                        const activo = r.activo ?? r.Activo ?? true;
                        return (
                            activo && String(idSol) === String(sessionSolicitanteId)
                        );
                    })
                    .map((r) => {
                        const uId =
                            r.idUnidadOrganizacional ??
                            r.IdUnidadOrganizacional ??
                            r["idUnidadOrganizacional"] ??
                            r["IdUnidadOrganizacional"];
                        const num = Number(uId);
                        return num || null;
                    })
                    .filter(Boolean)
            ),
        ];

        setUnidadesOrgPermitidasIds(ids);
    }, [sessionSolicitanteId, relSolicitanteUO]);

    // 🆕 Lista de choferes filtrados por UO permitidas (Solicitante ↔ UO + Empleado ↔ UO)
    const choferesFiltrados = useMemo(() => {
        if (!choferes.length) return [];

        // Si no hay solicitante, no dejamos elegir chofer
        if (!sessionSolicitanteId) return [];

        // Si no tenemos UO permitidas o relaciones, tampoco mostramos choferes
        if (
            !unidadesOrgPermitidasIds.length ||
            !allEmpleadosUnidadOrg.length
        ) {
            return [];
        }

        // Empleados que pertenecen a alguna UO permitida
        const empleadosPermitidos = new Set(
            allEmpleadosUnidadOrg
                .filter((rel) => {
                    const empId =
                        rel.idEmpleado ??
                        rel.IdEmpleado ??
                        rel["idEmpleado"] ??
                        rel["IdEmpleado"];
                    const uId =
                        rel.idUnidadOrganizacional ??
                        rel.IdUnidadOrganizacional ??
                        rel["idUnidadOrganizacional"] ??
                        rel["IdUnidadOrganizacional"];
                    const activo = rel.activo ?? rel.Activo ?? true;
                    const uNum = Number(uId);
                    return (
                        empId && uNum && unidadesOrgPermitidasIds.includes(uNum)
                    );
                })
                .map((rel) => {
                    const empId =
                        rel.idEmpleado ??
                        rel.IdEmpleado ??
                        rel["idEmpleado"] ??
                        rel["IdEmpleado"];
                    return Number(empId) || null;
                })
                .filter(Boolean)
        );

        let base = choferes.filter((ch) => {
            const empId =
                ch.idEmpleado ??
                ch.IdEmpleado ??
                ch["idEmpleado"] ??
                ch["IdEmpleado"];
            const empNum = Number(empId);
            if (!empNum) return false;
            return empleadosPermitidos.has(empNum);
        });

        // Pequeño "escape" para edición: si la reserva que estoy editando
        // tiene un chofer que ya no entra en el filtro, lo agregamos
        if (form.idChofer && !base.some((c) => c.idChofer === form.idChofer)) {
            const fallback = choferes.find(
                (c) => c.idChofer === form.idChofer
            );
            if (fallback) {
                base = [...base, fallback];
            }
        }

        return base;
    }, [
        choferes,
        allEmpleadosUnidadOrg,
        unidadesOrgPermitidasIds,
        sessionSolicitanteId,
        form.idChofer,
    ]);

    // 🆕 Mapeo de reservas → eventos ENRIQUECIENDO el nombre del chofer
    useEffect(() => {
        if (hasSessionErrors) {
            setEvents([]);
            return;
        }

        const mapped = (reservas || []).map((r) => {
            const ev = mapReservaToEvent(r);

            // Si meta.chofer viene vacío, lo rellenamos usando la lista de choferes
            if (
                (!ev.meta.chofer || !ev.meta.chofer.trim()) &&
                ev.meta.idChofer &&
                choferes.length
            ) {
                const ch = choferes.find(
                    (c) => c.idChofer === ev.meta.idChofer
                );
                if (ch) {
                    ev.meta.chofer = ch.nombreCompleto || "";
                }
            }

            return ev;
        });

        const filtered = filterEventsForView(
            mapped,
            showRejected,
            showOnlyMine, // 🆕 Parámetro para filtrar solo mis reservas
            sessionSolicitanteId,
            form.idChofer || 0
        );

        setEvents(filtered);
    }, [
        reservas,
        showRejected,
        showOnlyMine, // 🆕 Dependencia agregada
        sessionSolicitanteId,
        form.idChofer,
        choferes,
        hasSessionErrors,
    ]);

    const selectedDate = useMemo(() => {
        if (!form.fecha || typeof form.fecha !== 'string' || form.fecha.trim() === '') {
            return new Date(); // fallback a hoy si no hay fecha válida
        }
        const d = new Date(form.fecha + "T00:00:00");
        if (Number.isNaN(d.getTime())) {
            return new Date(); // fallback si la fecha es inválida
        }
        return d;
    }, [form.fecha]);
    const [driverIntervals, setDriverIntervals] = useState([]);

    // Recalcular driverIntervals a partir de BD (empleadosTurnos + turnos) y chofer seleccionado
    useEffect(() => {
        if (!choferes.length || !turnos.length || !empleadosTurnos.length) {
            setDriverIntervals([]);
            return;
        }

        let ch = null;

        if (form.idChofer) {
            ch = choferes.find((c) => c.idChofer === form.idChofer) || null;
        }

        if (!ch && form.chofer) {
            const target = (form.chofer || "").trim().toLowerCase();
            ch =
                choferes.find(
                    (c) => (c.nombreCompleto || "").trim().toLowerCase() === target
                ) || null;
        }

        if (!ch || !ch.idEmpleado) {
            setDriverIntervals([]);
            return;
        }

        // 🔹 Determinamos la fecha objetivo de la reserva (o 'hoy' si no hay fecha seleccionada)
        let targetDate = new Date();
        targetDate.setHours(0, 0, 0, 0);

        if (form.fecha && typeof form.fecha === 'string') {
            const d = new Date(form.fecha + "T00:00:00");
            if (!isNaN(d.getTime())) {
                targetDate = d;
            }
        }
        const targetTime = targetDate.getTime();

        // 🔹 1. Obtener todas las asignaciones del chofer
        const todasAsignaciones = empleadosTurnos.filter(
            (et) => et.idEmpleado === ch.idEmpleado
        );

        if (!todasAsignaciones.length) {
            setDriverIntervals([]);
            return;
        }

        // 🔹 2. Preparar asignaciones con su fecha normalizada
        // Si fechaAsignacion es null, asumimos que es un registro antiguo (inicio de los tiempos)
        const asignacionesConFecha = todasAsignaciones.map(a => {
            let fecha = new Date(0); // Epoch 1970 (para legacy/null)
            if (a.fechaAsignacion) {
                // Asumiendo formato ISO o string compatible
                fecha = new Date(a.fechaAsignacion);
            }
            // Normalizar a medianoche para comparar solo días
            fecha.setHours(0, 0, 0, 0);
            return { ...a, _fechaSnapshot: fecha.getTime() };
        });

        // 🔹 3. Encontrar las fechas de snapshot únicas disponibles
        const fechasSnapshots = [...new Set(asignacionesConFecha.map(a => a._fechaSnapshot))];

        // 🔹 4. Ordenar descendente (más recientes primero)
        fechasSnapshots.sort((a, b) => b - a);

        // 🔹 5. Encontrar el snapshot vigente más reciente (primer snapshot <= targetDate)
        const snapshotVigente = fechasSnapshots.find(f => f <= targetTime);

        if (snapshotVigente === undefined) {
            // No hay ninguna asignación vigente para esta fecha (todas son futuras)
            setDriverIntervals([]);
            return;
        }

        // 🔹 6. Filtrar las asignaciones que pertenecen a ese snapshot
        const asignacionesVigentes = asignacionesConFecha.filter(a => a._fechaSnapshot === snapshotVigente);

        const turnoIds = [
            ...new Set(asignacionesVigentes.map((a) => a.idTurno).filter(Boolean)),
        ];

        if (!turnoIds.length) {
            setDriverIntervals([]);
            return;
        }

        const intervals = turnoIds
            .map((idT) => turnos.find((t) => t.idTurno === idT))
            .filter(Boolean)
            .map((t) => ({
                start: String(t.horaInicio || "").slice(0, 5),
                end: String(t.horaFin || "").slice(0, 5),
            }));

        setDriverIntervals(intervals);
    }, [form.idChofer, form.chofer, choferes, turnos, empleadosTurnos, form.fecha]);

    // Intervalos REALES para la fecha seleccionada (aplicando excepciones)
    const intervalsForSelectedDate = useMemo(() => {
        if (!form.idChofer || !form.fecha) return driverIntervals;

        // Validar que la fecha sea válida antes de crear el objeto Date
        if (typeof form.fecha !== 'string' || form.fecha.trim() === '') {
            return driverIntervals;
        }

        const d = new Date(form.fecha + "T00:00:00");
        if (Number.isNaN(d.getTime())) {
            return driverIntervals; // si la fecha es inválida, retornar intervalos base
        }

        return buildDailyIntervalsForDriver(
            d,
            form.idChofer,
            driverIntervals,
            excepcionesTurno
        );
    }, [form.idChofer, form.fecha, driverIntervals, excepcionesTurno]);

    // Sincronizar chofer y emailChofer cuando tenemos idChofer y choferes filtrados
    useEffect(() => {
        if (!form.idChofer || !choferesFiltrados.length) return;

        const ch = choferesFiltrados.find((c) => c.idChofer === form.idChofer);
        if (!ch) return;

        setForm((f) => {
            if (f.chofer === ch.nombre && f.emailChofer === ch.correo) {
                return f;
            }
            return {
                ...f,
                chofer: ch.nombre,
                emailChofer: ch.correo || "",
            };
        });
    }, [form.idChofer, choferesFiltrados]);

    // 🚀 OPTIMIZACIÓN: Cargar disponibilidad específica cuando cambia el chofer
    useEffect(() => {
        if (form.idChofer) {
            loadDriverAvailability(Number(form.idChofer));
        }
    }, [form.idChofer]);

    const duracion = useMemo(() => {
        if (!form.horaInicio || !form.horaFin || !form.fecha || !form.fechaFin) return { h: 0, m: 0, days: 0 };

        const ini = toDateWithTime(form.fecha, form.horaInicio);
        const fin = toDateWithTime(form.fechaFin, form.horaFin);

        const ms = fin - ini;

        if (ms < 0) {
            return { h: 0, m: 0, days: 0 };
        }

        const totalHours = Math.floor(ms / 3_600_000);
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        const minutes = Math.floor((ms % 3_600_000) / 60_000);

        return {
            h: hours,
            m: minutes,
            days: days,
            totalHours: totalHours
        };
    }, [form.fecha, form.fechaFin, form.horaInicio, form.horaFin]);

    // 🆕 Validación: Duración mínima de 10 minutos
    const isDurationTooShort = useMemo(() => {
        if (!form.horaInicio || !form.horaFin || !form.fecha || !form.fechaFin) return false;

        const ini = toDateWithTime(form.fecha, form.horaInicio);
        const fin = toDateWithTime(form.fechaFin, form.horaFin);
        const ms = fin - ini;

        // 10 minutos = 600,000 ms
        return ms < 600000;
    }, [form.fecha, form.fechaFin, form.horaInicio, form.horaFin]);

    // 🔹 Extras acumuladas del chofer para la fecha seleccionada
    const extrasContext = useMemo(() => {
        if (!form.idChofer || !form.fecha) {
            return { semanaActual: 0, mesActual: 0 };
        }

        if (!extrasMes.length && !extrasSemana.length) {
            return { semanaActual: 0, mesActual: 0 };
        }

        const d = new Date(form.fecha + "T00:00:00");
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const week = getISOWeek(d);

        const rowsMes = extrasMes.filter(
            (row) =>
                row.idChofer === form.idChofer &&
                Number(row.anio) === year &&
                Number(row.mes) === month
        );
        const mesActual = rowsMes.reduce(
            (acc, row) => acc + (Number(row.horasExtras) || 0),
            0
        );

        const rowsSemana = extrasSemana.filter(
            (row) =>
                row.idChofer === form.idChofer &&
                Number(row.anio) === year &&
                Number(row.semana) === week
        );
        const semanaActual = rowsSemana.reduce(
            (acc, row) => acc + (Number(row.horasExtras) || 0),
            0
        );

        return { semanaActual, mesActual };
    }, [form.idChofer, form.fecha, extrasMes, extrasSemana]);

    const selectedRangeLabel = useMemo(
        () =>
            formatReservationRange(
                form.fecha,
                form.horaInicio,
                form.fechaFin,
                form.horaFin
            ),
        [form.fecha, form.fechaFin, form.horaInicio, form.horaFin]
    );

    // 🆕 Límites de horas extra según ParametrosLaborales/UO del chofer
    const extrasLimits = useMemo(() => {
        if (
            !form.idChofer ||
            !form.fecha ||
            !parametrosLaborales.length ||
            !allEmpleadosUnidadOrg.length ||
            !choferes.length
        ) {
            return { semanaMax: null, mesMax: null };
        }

        const ch = choferes.find((c) => c.idChofer === form.idChofer);
        if (!ch || !ch.idEmpleado) {
            return { semanaMax: null, mesMax: null };
        }

        const empleadoId = Number(ch.idEmpleado);
        if (!empleadoId) {
            return { semanaMax: null, mesMax: null };
        }

        // UO del empleado/chofer
        const uoIds = [
            ...new Set(
                allEmpleadosUnidadOrg
                    .filter((rel) => {
                        const empId =
                            rel.idEmpleado ??
                            rel.IdEmpleado ??
                            rel["idEmpleado"] ??
                            rel["IdEmpleado"];
                        return Number(empId) === empleadoId;
                    })
                    .map((rel) => {
                        const uId =
                            rel.idUnidadOrganizacional ??
                            rel.IdUnidadOrganizacional ??
                            rel["idUnidadOrganizacional"] ??
                            rel["IdUnidadOrganizacional"];
                        return Number(uId) || null;
                    })
                    .filter(Boolean)
            ),
        ];

        if (!uoIds.length) {
            return { semanaMax: null, mesMax: null };
        }

        const candidates = parametrosLaborales.filter((p) => {
            const pUoId = Number(p.idUnidadOrganizacional || 0);
            return pUoId && uoIds.includes(pUoId);
        });

        if (!candidates.length) {
            return { semanaMax: null, mesMax: null };
        }

        // 🔹 Las vigencias son infinitas, simplemente tomamos el primer parámetro
        const chosen = candidates[0];

        const semanaMax = Number(chosen.topeSemanalMin) || 0;
        const mesMax = Number(chosen.topeMensualMin) || 0;

        return { semanaMax, mesMax };
    }, [
        form.idChofer,
        form.fecha,
        parametrosLaborales,
        allEmpleadosUnidadOrg,
        choferes,
    ]);

    const setF = (k) => (e) =>
        setForm((f) => ({
            ...f,
            [k]: e.target.value,
        }));

    // 🆕 Checador de días festivos contra la tabla de BD
    const isHolidayDate = useCallback(
        (date) => {
            if (!date || !diasFestivos || diasFestivos.length === 0) {
                return false;
            }

            const target = formatDateLocal(date); // "YYYY-MM-DD"
            const [ty, tm, td] = target.split("-");

            return diasFestivos.some((d) => {
                const raw = d.fechaFestiva
                    ? String(d.fechaFestiva).slice(0, 10)
                    : null;
                if (!raw) return false;

                const [y, m, day] = raw.split("-");
                if (!m || !day) return false;

                // Si es recurrente, el día aplica cada año (mismo mes/día)
                if (d.esRecurrente) {
                    return m === tm && day === td;
                }

                // Si no es recurrente, debe coincidir la fecha exacta
                return raw === target;
            });
        },
        [diasFestivos]
    );

    // ¿Está dentro del horario? (usando horario real con excepciones)
    const isInside = useMemo(() => {
        if (!form.chofer || !form.horaInicio || !form.horaFin) return null;
        return isInsideInterval(
            form.horaInicio,
            form.horaFin,
            intervalsForSelectedDate
        );
    }, [form.chofer, form.horaInicio, form.horaFin, intervalsForSelectedDate]);

    const suggestedStatus = useMemo(() => {
        if (isInside === null) return null;
        return isInside ? "APROBADA" : "PENDIENTE";
    }, [isInside]);

    const fueraHorarioMinFlag = useMemo(() => {
        if (isInside === null) return null;
        return !isInside;
    }, [isInside]);

    // 🆕 Detectar si hay problema de tiempo de anticipación (tiene prioridad sobre todo)
    const timeAnticipationIssue = useMemo(() => {
        if (!form.fecha || !form.horaInicio) return null;

        const start = toDateWithTime(form.fecha, form.horaInicio);
        const now = new Date();
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);

        // Solo aplica para reservas del día actual
        if (startOfDay(start).getTime() !== todayStart.getTime()) {
            return null;
        }

        // Calcular si ya expiró el margen de 10 minutos
        const tenMinutesAfterStart = new Date(start.getTime() + 10 * 60 * 1000);

        if (now > tenMinutesAfterStart) {
            const selectedTime = start.toLocaleTimeString('es-MX', {
                hour: '2-digit',
                minute: '2-digit'
            });
            const currentTime = now.toLocaleTimeString('es-MX', {
                hour: '2-digit',
                minute: '2-digit'
            });
            const deadlineTime = tenMinutesAfterStart.toLocaleTimeString('es-MX', {
                hour: '2-digit',
                minute: '2-digit'
            });

            return {
                hasIssue: true,
                selectedTime,
                currentTime,
                deadlineTime,
                message: `⚠️ TIEMPO EXPIRADO: La hora seleccionada (${selectedTime}) ya no está disponible. Límite: ${deadlineTime} | Actual: ${currentTime}. Selecciona una hora más adelante.`
            };
        }

        return null;
    }, [form.fecha, form.horaInicio]);


    // 🔹 Tipo de aprobación sugerido para esta reserva
    const approvalInfo = useMemo(() => {
        // Falta información básica
        if (!form.idChofer || !form.fecha || !form.horaInicio || !form.horaFin) {
            return {
                tipoId: null,
                label: "",
                weeklyBefore: 0,
                monthlyBefore: 0,
                weeklyAfter: 0,
                monthlyAfter: 0,
                extraHours: 0,
                esFinDeSemana: false,
                esFestivo: false,
            };
        }

        // Evitar falsos positivos si horaInicio == horaFin (se validará al guardar)
        if (form.horaInicio === form.horaFin) {
            return {
                tipoId: null,
                label: "",
                weeklyBefore: 0,
                monthlyBefore: 0,
                weeklyAfter: 0,
                monthlyAfter: 0,
                extraHours: 0,
                esFinDeSemana: false,
                esFestivo: false,
            };
        }

        const fechaReserva = new Date(form.fecha + "T00:00:00");
        const esFinDeSemana = isWeekend(fechaReserva);
        const esFestivo = isHolidayDate(fechaReserva);

        // Horas de ESTA reserva (aprox) → si está fuera de horario todo se considera extra
        const extraHours =
            isInside === false ? duracion.h + duracion.m / 60 : 0;

        const weeklyBefore = extrasContext.semanaActual || 0;
        const monthlyBefore = extrasContext.mesActual || 0;

        const weeklyAfter = weeklyBefore + extraHours;
        const monthlyAfter = monthlyBefore + extraHours;

        let tipoId = null;
        let label = "";

        // 0) Verificar si es una reserva de varios días (fecha inicio != fecha fin)
        const esVariosDias = form.fecha !== form.fechaFin;

        if (esVariosDias) {
            tipoId = TIPO_APROBACION.VARIOS_DIAS;
            label = "Viaje foráneo (varios días): la reserva se enviará a aprobación especial.";
        }
        // 1) Días festivos (cargados desde BD)
        else if (esFestivo) {
            tipoId = TIPO_APROBACION.DIAS_FESTIVOS;
            label = "Día festivo: la reserva se enviará a aprobación especial.";
        }
        // 2) Fin de semana (sábado / domingo)
        else if (esFinDeSemana) {
            tipoId = TIPO_APROBACION.FIN_SEMANA;
            label = "Fin de semana: la reserva se enviará a aprobación especial.";
        }
        // 3) Flujos de horas extra (solo si está fuera de horario)
        else if (isInside === false) {
            const semanaMax = extrasLimits.semanaMax;
            const mesMax = extrasLimits.mesMax;

            if (mesMax != null && mesMax > 0 && monthlyAfter > mesMax) {
                tipoId = TIPO_APROBACION.HORAS_MES_EXCEDIDAS;
                label = `Horas mensuales excedidas: con esta reserva llegarías a ~${monthlyAfter.toFixed(
                    1
                )} h extra en el mes (límite ${mesMax} h).`;
            } else if (semanaMax != null && semanaMax > 0 && weeklyAfter > semanaMax) {
                tipoId = TIPO_APROBACION.HORAS_SEMANA_EXCEDIDAS;
                label = `Horas semanales excedidas: con esta reserva llegarías a ~${weeklyAfter.toFixed(
                    1
                )} h extra en la semana (límite ${semanaMax} h).`;
            } else {
                tipoId = TIPO_APROBACION.FUERA_HORARIO;

                if ((semanaMax != null && semanaMax > 0) || (mesMax != null && mesMax > 0)) {
                    const parts = [];
                    if (semanaMax != null && semanaMax > 0) {
                        parts.push(`semanales: ${semanaMax} h`);
                    }
                    if (mesMax != null && mesMax > 0) {
                        parts.push(`mensuales: ${mesMax} h`);
                    }
                    const textoLimites = parts.length ? ` (límites ${parts.join(" / ")})` : "";
                    label =
                        "Fuera de horario: dentro del límite de horas extra definidas en los parámetros laborales" +
                        textoLimites +
                        ".";
                } else {
                    label =
                        "Fuera de horario: no se encontraron parámetros laborales configurados para este chofer.";
                }
            }
        }

        return {
            tipoId,
            label,
            weeklyBefore,
            monthlyBefore,
            weeklyAfter,
            monthlyAfter,
            extraHours,
            esFinDeSemana,
            esFestivo,
        };
    }, [
        form.idChofer,
        form.fecha,
        form.fechaFin,
        form.horaInicio,
        form.horaFin,
        duracion,
        isInside,
        extrasContext,
        extrasLimits,
        isHolidayDate,
    ]);

    const hasCriticalChanges = useMemo(() => {
        if (!originalRes) return false;
        const origDate = formatDateLocal(originalRes.start);
        const origEndDate = formatDateLocal(originalRes.end);
        const origStart = originalRes.start.toTimeString().slice(0, 5);
        const origEnd = originalRes.end.toTimeString().slice(0, 5);
        const origDriver = originalRes.meta?.chofer || "";

        // Parse origin/destination from original metadata
        const getAddr = (loc, txt) => {
            if (loc && loc.address) return loc.address;
            try { return JSON.parse(txt).address; } catch { return txt || ""; }
        };
        const origMeta = originalRes.meta || {};
        const origOriginAddr = getAddr(origMeta.origin, origMeta.origenTexto);
        const origDestAddr = getAddr(origMeta.destination, origMeta.destinoTexto);

        return (
            form.fecha !== origDate ||
            form.fechaFin !== origEndDate ||
            form.horaInicio !== origStart ||
            form.horaFin !== origEnd ||
            form.chofer !== origDriver ||
            (origin.address || "").trim() !== (origOriginAddr || "").trim() ||
            (destination.address || "").trim() !== (origDestAddr || "").trim()
        );
    }, [
        originalRes,
        form.fecha,
        form.fechaFin,
        form.horaInicio,
        form.horaFin,
        form.chofer,
        origin.address,
        destination.address
    ]);

    const hasChanges = useMemo(() => {
        if (!originalRes) return false;

        const origMeta = originalRes.meta || {};
        const motifChanged = (form.motivo || "").trim() !== (origMeta.motivo || "").trim();
        const obsChanged = (form.observaciones || "").trim() !== (origMeta.observaciones || "").trim();

        return hasCriticalChanges || motifChanged || obsChanged;
    }, [hasCriticalChanges, form.motivo, form.observaciones, originalRes]);


    const predictedEstadoId = useMemo(() => {
        // Si hay algún tipo de aprobación, siempre queda como Pendiente (1)
        if (approvalInfo.tipoId != null) return 1;

        if (isInside === true) return 3; // dentro de horario y sin aprobaciones → confirmada
        if (isInside === false) return 1; // fuera de horario → pendiente
        return null;
    }, [approvalInfo.tipoId, isInside]);

    const effectiveEstadoId = useMemo(() => {
        // Si ya hay decisión de RH y NO hay cambios críticos, mantenemos el estado
        if (hasRhDecision && !hasCriticalChanges && savedEstadoId != null) {
            return savedEstadoId;
        }
        if (predictedEstadoId != null) return predictedEstadoId;
        return savedEstadoId;
    }, [hasRhDecision, hasCriticalChanges, savedEstadoId, predictedEstadoId]);

    const displayStatusInfo = useMemo(() => {
        if (effectiveEstadoId == null) return null;
        return getEstadoInfo(effectiveEstadoId);
    }, [effectiveEstadoId]);

    // Disponibilidad semanal en calendario (aplicando excepciones por día)
    // Ahora calcula el snapshot vigente POR DÍA usando fechaAsignacion
    const availabilityBlocks = useMemo(() => {
        if (!form.chofer || !showSchedule || !form.idChofer) {
            return [];
        }

        const blocks = [];
        const baseMonday = getMonday(anchor);

        // Resolver chofer → empleado
        let ch = null;
        if (form.idChofer) {
            ch = choferes.find((c) => c.idChofer === form.idChofer) || null;
        }

        if (!ch || !ch.idEmpleado) {
            return [];
        }

        const empleadoId = Number(ch.idEmpleado);
        if (!empleadoId || !empleadosTurnos.length || !turnos.length) {
            return [];
        }

        // Todas las asignaciones del chofer
        const todasAsignaciones = empleadosTurnos.filter(
            (et) => Number(et.idEmpleado) === empleadoId
        );

        if (!todasAsignaciones.length) {
            return [];
        }

        // Normalizar snapshots por día
        const asignacionesConFecha = todasAsignaciones.map(a => {
            let fecha = new Date(0); // legacy si no hay fecha
            if (a.fechaAsignacion) {
                const d = new Date(a.fechaAsignacion);
                if (!isNaN(d.getTime())) {
                    fecha = d;
                }
            }
            fecha.setHours(0, 0, 0, 0);
            return { ...a, _fechaSnapshot: fecha.getTime() };
        });

        const fechasSnapshots = [...new Set(asignacionesConFecha.map(a => a._fechaSnapshot))]
            .sort((a, b) => b - a); // más recientes primero

        for (let i = 0; i < 7; i++) {
            const date = new Date(baseMonday);
            date.setDate(baseMonday.getDate() + i);
            const dow = date.getDay();

            // omitimos fines de semana en disponibilidad visual
            if (dow === 0 || dow === 6) continue;

            date.setHours(0, 0, 0, 0);
            const targetTime = date.getTime();

            // Snapshot vigente para ESTE día
            // Buscamos el snapshot más reciente que sea MENOR O IGUAL al día actual (targetTime).
            // FechasSnapshots ya viene ordenado DESC (más reciente -> más antiguo).
            const snapshotVigente = fechasSnapshots.find(f => f <= targetTime + 86400000); // Allow margin/same day effectively
            if (snapshotVigente === undefined) {
                continue;
            }

            const asignacionesVigentes = asignacionesConFecha.filter(
                a => a._fechaSnapshot === snapshotVigente
            );

            const turnoIds = [
                ...new Set(asignacionesVigentes.map((a) => a.idTurno).filter(Boolean)),
            ];

            if (!turnoIds.length) {
                continue;
            }

            const baseIntervals = turnoIds
                .map((idT) => turnos.find((t) => t.idTurno == idT)) // Loose equality for string/int match
                .filter(Boolean)
                .map((t) => ({
                    start: String(t.horaInicio || "").slice(0, 5),
                    end: String(t.horaFin || "").slice(0, 5),
                }));

            if (!baseIntervals.length) {
                continue;
            }

            const dateStr = formatDateLocal(date);
            const ivs = buildDailyIntervalsForDriver(
                date,
                form.idChofer,
                baseIntervals,
                excepcionesTurno
            );

            ivs.forEach((iv, idx) => {
                blocks.push({
                    id: `av-${i}-${idx}`,
                    start: toDateWithTime(dateStr, iv.start),
                    end: toDateWithTime(dateStr, iv.end),
                    color: driverColor,
                });
            });
        }

        return blocks;
    }, [
        form.chofer,
        form.idChofer,
        showSchedule,
        anchor,
        choferes,
        empleadosTurnos,
        turnos,
        driverColor,
        excepcionesTurno,
    ]);

    function validate() {
        if (hasSessionErrors) {
            setAlertDialog({
                open: true,
                title: "Error de Sesión",
                message: "No es posible registrar reservas porque falta información importante de tu usuario en el sistema.",
                variant: "error"
            });
            return null;
        }

        if (!form.idSolicitante) {
            setAlertDialog({
                open: true,
                title: "Error",
                message: "No fue posible identificarte como solicitante en el sistema. Por favor contacta a sistemas.",
                variant: "error"
            });
            return null;
        }

        const start = toDateWithTime(form.fecha, form.horaInicio);
        const end = toDateWithTime(form.fechaFin, form.horaFin);

        if (!form.horaInicio || !form.horaFin) {
            setAlertDialog({ open: true, title: 'Falta Información', message: "Define hora de inicio y fin.", variant: 'warning' });
            return null;
        }

        if (end <= start) {
            setAlertDialog({ open: true, title: 'Error de Validación', message: "La fecha/hora de fin debe ser posterior a la de inicio.", variant: 'error' });
            return null;
        }

        if (startOfDay(start) < today()) {
            setAlertDialog({ open: true, title: 'Fecha Inválida', message: "No puedes reservar fechas pasadas.", variant: 'error' });
            return null;
        }

        // 🆕 Validación de tiempo de anticipación para el día actual
        // El usuario puede seleccionar una hora y tiene 10 minutos para completar el registro
        const now = new Date();
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);

        // Si la reserva es para hoy
        if (startOfDay(start).getTime() === todayStart.getTime()) {
            // Permitir un margen de 10 minutos después de la hora seleccionada
            // Ejemplo: si selecciona 10:30, puede guardar hasta las 10:40
            const tenMinutesAfterStart = new Date(start.getTime() + 10 * 60 * 1000);

            if (now > tenMinutesAfterStart) {
                const selectedTime = start.toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                const currentTime = now.toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                const deadlineTime = tenMinutesAfterStart.toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                setAlertDialog({
                    open: true,
                    title: "⚠️ TIEMPO DE RESERVA EXPIRADO ⚠️",
                    message: `La hora seleccionada (${selectedTime}) ya no está disponible.\n\n` +
                        `📌 DETALLES:\n` +
                        `   • Hora seleccionada: ${selectedTime}\n` +
                        `   • Límite para guardar: ${deadlineTime}\n` +
                        `   • Hora actual: ${currentTime}\n\n` +
                        `💡 SOLUCIÓN:\n` +
                        `Selecciona una hora más adelante para poder completar tu reserva.\n` +
                        `Recuerda: tienes 10 minutos desde la hora seleccionada para guardar la reserva.`,
                    variant: "warning"
                });
                return null;
            }
        }

        if (!origin.address || !destination.address) {
            setAlertDialog({ open: true, title: 'Falta Información', message: "Selecciona origen y destino.", variant: 'warning' });
            return null;
        }
        if (!form.idChofer) {
            setAlertDialog({ open: true, title: 'Falta Información', message: "Selecciona un chofer.", variant: 'warning' });
            return null;
        }

        if (!form.motivo || !form.motivo.trim()) {
            setAlertDialog({ open: true, title: 'Datos Faltantes', message: "Por favor, ingresa el motivo del viaje. Este campo es obligatorio.", variant: 'warning' });
            return null;
        }

        // 🚫 Choque con otra reserva del mismo chofer
        if (hasOverlap) {
            setAlertDialog({
                open: true,
                title: "Horario Ocupado",
                message: "El chofer ya tiene una reserva en ese horario. Ajusta la hora o elige otro chofer.",
                variant: "error"
            });
            return null;
        }

        return { start, end };
    }

    function jumpToWeekOf(date) {
        setAnchor(new Date(date));
    }

    function resetForm() {
        setEditingId(null);
        setCanEdit(true); // volvemos a permitir edición (modo nueva reserva)
        const today = formatDateLocal(new Date());
        setForm((f) => ({
            // mantenemos idSolicitante, nombre y email (de la sesión)
            ...f,
            fecha: today,
            fechaFin: today,
            horaInicio: "",
            horaFin: "",
            origenTexto: "",
            destinoTexto: "",
            idChofer: 0,
            motivo: "",
            observaciones: "",
            chofer: "",
            emailChofer: "",
        }));
        setOrigin({ address: "", location: null });
        setDestination({ address: "", location: null });
        setShowSchedule(false);
        setSavedStatus(null);
        setSavedEstadoId(null);
        setHasRhDecision(false);
        setOriginalRes(null);
    }

    // Estados para modales de alerta y confirmación
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'warning' });


    // Lógica real de guardado (extraída de handleSubmit)
    const executeSave = async (vr) => { // 'vr' (validation result) passed as argument
        let idEstadoCalculated = predictedEstadoId != null ? predictedEstadoId : 1;

        // 🛡️ Si estamos editando y NO hay cambios críticos (fecha, hora, chofer),
        // preservamos el estado actual (ej. Aprobada) en lugar de recalcularlo.
        // Esto cumple: "si solo cambia motivo/obs, solo se actualiza sin más".
        if (editingId && savedEstadoId != null && !hasCriticalChanges) {
            idEstadoCalculated = savedEstadoId;
        }

        const metaBase = {
            idSolicitante: form.idSolicitante,
            idChofer: form.idChofer,
            motivo: form.motivo,
            fechaInicio: formatDateLocal(vr.start), // Use vr.start
            fechaFin: formatDateLocal(vr.end),     // Use vr.end
            horaInicio: form.horaInicio,
            horaFin: form.horaFin,
            origenTexto: JSON.stringify(origin),
            destinoTexto: JSON.stringify(destination),
            idEstadoReserva: idEstadoCalculated,
            fueraHorarioMin: fueraHorarioMinFlag === null ? false : fueraHorarioMinFlag,
            observaciones: form.observaciones,
            idTipoAprobacion: approvalInfo.tipoId ?? null,
        };

        try {
            if (editingId) {

                const payload = { ...metaBase, idReserva: editingId };
                await axios.put(enpointReservas, payload);
            } else {
                await axios.post(enpointReservas, metaBase);
            }
            await loadDataReservas();
            // Actualizar disponibilidad del chofer también
            if (form.idChofer) {
                await loadDriverAvailability(form.idChofer);
            }
            resetForm();
            setAlertDialog({ open: true, title: 'Éxito', message: 'Reserva guardada correctamente.', variant: 'success' });
        } catch (err) {
            console.error(err);
            setAlertDialog({ open: true, title: 'Error', message: 'Error al guardar la reserva.', variant: 'error' });
        }
    };

    async function handleSubmit(e) {
        e.preventDefault();

        if (hasSessionErrors) {
            setAlertDialog({ open: true, title: 'Error de Sesión', message: "No es posible guardar porque falta información de tu usuario.", variant: 'error' });
            return;
        }

        if (editingId && !canEdit) {
            setAlertDialog({ open: true, title: 'Acceso Denegado', message: "No puedes modificar esta reserva porque pertenece a otro solicitante.", variant: 'error' });
            return;
        }

        const vr = validate();
        if (!vr) return;

        // Validación de cambios (sin cambios)
        // 🛡️ Usamos la reserva original desde el state (reservas) para evitar problemas de mutación en originalRes
        let comparisonEvent = originalRes;
        if (editingId && reservas.length > 0) {
            const raw = reservas.find(r => r.idReserva === editingId);
            if (raw) {
                comparisonEvent = mapReservaToEvent(raw);
            }
        }

        if (editingId && comparisonEvent) {
            const origMeta = comparisonEvent.meta || {};
            const getAddr = (loc, txt) => {
                if (loc && loc.address) return loc.address;
                try { return JSON.parse(txt).address; } catch { return txt || ""; }
            };
            const origDateStr = formatDateLocal(comparisonEvent.start);
            const origEndDateStr = formatDateLocal(comparisonEvent.end);
            const origStartStr = comparisonEvent.start.toTimeString().slice(0, 5);
            const origEndStr = comparisonEvent.end.toTimeString().slice(0, 5);
            const origOriginAddr = getAddr(origMeta.origin, origMeta.origenTexto);
            const origDestAddr = getAddr(origMeta.destination, origMeta.destinoTexto);

            console.log("[DEBUG] Comparando cambios:", {
                form: { ...form, origin: origin.address, dest: destination.address },
                orig: {
                    idChofer: origMeta.idChofer,
                    fecha: origDateStr,
                    fechaFin: origEndDateStr,
                    horaInicio: origStartStr,
                    horaFin: origEndStr,
                    origin: origOriginAddr,
                    dest: origDestAddr
                }
            });

        }

        // 1. Caso: Modificación de reserva YA APROBADA con cambios CRÍTICOS (reinicia flujo)
        // ⚠️ Solo si hay cambios críticos (chofer, fecha, hora, ubicación). 
        // Cambios solo de motivo/observaciones NO entran aquí.
        if (editingId && savedEstadoId === 3 && hasCriticalChanges) {
            setConfirmDialog({
                open: true,
                title: 'Modificar Reserva Aprobada',
                message: 'Esta reserva ya fue aprobada. Al modificar los detalles del viaje (fecha, hora, chofer), se perderá la aprobación actual y deberá pasar nuevamente por el proceso de validación. ¿Deseas continuar?',
                variant: 'warning',
                onConfirm: async () => {
                    await executeSave(vr);
                    setConfirmDialog(prev => ({ ...prev, open: false }));
                }
            });
            return;
        }

        // 2. Caso: Nueva reserva o In-review que requiere Aprobación Especial.
        // Solo mostramos este modal si es NUEVA o si hubo CAMBIOS CRÍTICOS.
        if (approvalInfo.tipoId && (hasCriticalChanges || !editingId)) {
            setConfirmDialog({
                open: true,
                title: 'Requiere Aprobación',
                message: `Ten en cuenta: ${approvalInfo.label} Tu reserva quedará pendiente de aprobación. ¿Confirmar solicitud?`,
                variant: 'info',
                onConfirm: async () => {
                    await executeSave(vr);
                    setConfirmDialog(prev => ({ ...prev, open: false }));
                }
            });
            return;
        }

        // 3. Caso normal (sin aprobación especial o edición simple)
        await executeSave(vr);
    }

    // Actualización de onDelete
    const executeDelete = async () => {
        try {
            await axios.delete(`${enpointReservas}/${editingId}`);
            // Recargar disponibilidad del chofer también, por si liberamos cupo
            if (form.idChofer) {
                await loadDriverAvailability(form.idChofer);
            }
            await loadDataReservas();
            resetForm();
            setAlertDialog({ open: true, title: 'Eliminada', message: 'La reserva ha sido eliminada.', variant: 'success' });
        } catch (err) {
            console.error(err);
            setAlertDialog({ open: true, title: 'Error', message: 'Error al eliminar la reserva.', variant: 'error' });
        }
    };

    async function onDelete() {
        if (!editingId) return;
        if (hasSessionErrors) {
            setAlertDialog({ open: true, title: 'Error', message: "No es posible eliminar: faltan datos de sesión.", variant: 'error' });
            return;
        }
        if (!canEdit) {
            setAlertDialog({ open: true, title: 'Acceso Denegado', message: "No puedes eliminar esta reserva porque es ajena.", variant: 'error' });
            return;
        }

        const handleConfirmDelete = async () => {
            await executeDelete();
            setConfirmDialog(prev => ({ ...prev, open: false }));
        };

        // Mensaje personalizado si ya estaba aprobada
        if (savedEstadoId === 3) {
            setConfirmDialog({
                open: true,
                title: 'Eliminar Reserva Aprobada',
                message: 'Esta reservación ya ha sido aprobada. ¿Estás seguro de que deseas cancelarla? Se liberará el horario del chofer.',
                variant: 'warning',
                onConfirm: handleConfirmDelete
            });
        } else {
            setConfirmDialog({
                open: true,
                title: 'Eliminar Reserva',
                message: '¿Estás seguro de que deseas eliminar esta reservación?',
                variant: 'danger', // O warning
                onConfirm: handleConfirmDelete
            });
        }
    }

    function startEdit(evt) {
        if (hasSessionErrors) {
            setAlertDialog({ open: true, title: 'Error de Sesión', message: "No es posible editar reservas porque falta información importante de tu usuario en el sistema.", variant: 'error' });
            return;
        }

        // Mobile behavior: scroll to top and show popup
        if (isMobile) {
            setSelectedEvent(evt);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setShowEventPopup(true);
        }

        setEditingId(evt.id);

        const meta = evt.meta || {};
        const nombreChoferMeta = meta.chofer || "";

        // validación de “solo lectura” por idSolicitante
        const reservaSolicitanteId = meta.idSolicitante ?? null;
        setCanEdit(() => {
            if (!sessionSolicitanteId || !reservaSolicitanteId) {
                // si falta alguno, dejamos editar (caso legacy)
                return true;
            }
            return String(reservaSolicitanteId) === String(
                sessionSolicitanteId
            );
        });

        let currentDriverId = meta.idChofer || 0;
        let currentDriver = null;

        if (choferes.length) {
            if (currentDriverId) {
                currentDriver = choferes.find(
                    (c) => c.idChofer === currentDriverId
                );
            }

            if (!currentDriver && nombreChoferMeta) {
                currentDriver = choferes.find(
                    (c) => (c.nombre || "").trim() === nombreChoferMeta.trim()
                );
                if (currentDriver) {
                    currentDriverId = currentDriver.idChofer;
                }
            }
        }

        const choferNombre = nombreChoferMeta || currentDriver?.nombreCompleto || "";
        const choferCorreo = currentDriver?.correo || "";

        const fechaStr = formatDateLocal(evt.start);
        const fechaFinStr = formatDateLocal(evt.end);

        setForm((f) => ({
            ...f,
            // 👇 idSolicitante se mantiene igual (de la sesión)
            fecha: fechaStr,
            fechaFin: fechaFinStr,
            horaInicio: evt.start.toTimeString().slice(0, 5),
            horaFin: evt.end.toTimeString().slice(0, 5),
            origenTexto: "",
            destinoTexto: "",
            idChofer: currentDriverId || 0,
            motivo: meta.motivo || "",
            observaciones: meta.observaciones || "",
            chofer: choferNombre,
            emailChofer: choferCorreo,
        }));

        setOrigin(meta.origin || { address: "", location: null });
        setDestination(meta.destination || { address: "", location: null });
        jumpToWeekOf(evt.start);
        setShowSchedule(!!currentDriverId);

        const estado = (meta.estadoLabel || meta.estadoKey || "").toUpperCase();
        const rhTouched =
            !!meta.decidedAt ||
            !!meta.rhComment ||
            estado === "RECHAZADA" ||
            false;

        setSavedStatus(meta.estadoLabel || meta.estadoKey || null);
        const estadoIdFromMeta =
            meta.estadoId ?? meta.idEstadoReserva ?? null;
        setSavedEstadoId(estadoIdFromMeta);
        setHasRhDecision(rhTouched);
        setOriginalRes(evt);
    }



    const openPicker = (t) => {
        setPickerTarget(t);
        setPickerOpen(true);
    };

    const confirmPicker = (p) => {
        (pickerTarget === "origin" ? setOrigin : setDestination)(p);
        setPickerOpen(false);
    };

    function StatusBadge() {
        if (!displayStatusInfo) return null;

        return (
            <span
                className={`badge ${displayStatusInfo.cls}`}
                style={{ textAlign: "center" }}
            >
                {displayStatusInfo.label}
            </span>
        );
    }

    // 🔻🔻🔻 JSX COMPLETO 🔻🔻🔻
    return (
        <div className="page">
            <main className="content">
                {hasSessionErrors ? (
                    // 🔒 Bloqueo total - UI mejorada
                    <div style={{
                        gridColumn: '1 / -1',
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        minHeight: '80vh', padding: '2rem', width: '100%'
                    }}>
                        <div className="card" style={{
                            maxWidth: '800px', width: '100%',
                            padding: '3rem',
                            borderRadius: '16px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                            borderTop: '5px solid #ef4444'
                        }}>
                            <div style={{
                                width: '64px', height: '64px', borderRadius: '50%',
                                backgroundColor: '#fee2e2', color: '#dc2626',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 1.5rem auto', fontSize: '32px'
                            }}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                    <line x1="12" y1="9" x2="12" y2="13"></line>
                                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                </svg>
                            </div>
                            <h2 style={{
                                fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937',
                                marginBottom: '1rem'
                            }}>
                                Acceso Restringido
                            </h2>
                            <p style={{
                                color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6'
                            }}>
                                Lo sentimos, no podemos cargar el módulo de reservas porque tu usuario no cuenta con la configuración necesaria.
                            </p>

                            <div style={{
                                textAlign: 'left', backgroundColor: '#f9fafb',
                                padding: '1rem 1.5rem', borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                            }}>
                                <p style={{
                                    fontSize: '0.875rem', fontWeight: '600', color: '#374151',
                                    marginTop: 0, marginBottom: '0.5rem'
                                }}>
                                    Detalles del error:
                                </p>
                                <ul style={{
                                    margin: 0, paddingLeft: '1.25rem',
                                    fontSize: '0.875rem', color: '#ef4444'
                                }}>
                                    {sessionErrors.map((msg, idx) => (
                                        <li key={idx} style={{ marginBottom: '0.25rem' }}>{msg}</li>
                                    ))}
                                </ul>
                            </div>

                            <div style={{ marginTop: '2rem' }}>
                                <button
                                    className="btn-action"
                                    onClick={() => window.location.reload()}
                                >
                                    Intentar recargar
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <section className="form-card">
                            <form onSubmit={handleSubmit} className="grid-form">
                                {/* Información del usuario - OCULTA, ahora en modal */}

                                {/* Programación del servicio + Trayecto */}
                                <div className="reservation-section">
                                    <div className="reservation-section__header">
                                        <h2 className="reservation-section__title">
                                            PROGRAMACIÓN DEL SERVICIO
                                        </h2>
                                    </div>

                                    {isReadOnly && (
                                        <div className="alert alert-warning" role="alert">
                                            Esta reserva pertenece a otro solicitante. Solo puedes
                                            consultarla, no editarla.
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label htmlFor="chofer">Seleccionar chofer</label>
                                        <select
                                            id="chofer"
                                            className="form-control"
                                            value={form.idChofer || ""}
                                            disabled={isReadOnly}
                                            onChange={(e) => {
                                                const value = Number(e.target.value) || 0;
                                                const ch = choferesFiltrados.find(
                                                    (c) => c.idChofer === value
                                                );

                                                setForm((f) => ({
                                                    ...f,
                                                    idChofer: value,
                                                    chofer: ch?.nombreCompleto || "",
                                                    emailChofer: ch?.correo || "",
                                                }));
                                                setShowSchedule(!!value);
                                            }}
                                        >
                                            <option value="">
                                                {choferesFiltrados.length === 0
                                                    ? "No hay choferes disponibles"
                                                    : "Selecciona un chofer"}
                                            </option>
                                            {choferesFiltrados.map((chofer) => (
                                                <option key={chofer.idChofer} value={chofer.idChofer}>
                                                    {chofer.nombreCompleto}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {form.chofer && (
                                        <div className="form-group">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="user-info-label">Correo del chofer</div>
                                                    <div className="user-info-value">
                                                        {form.emailChofer || "No disponible"}
                                                    </div>
                                                </div>
                                                <br></br>
                                                <button
                                                    type="button"
                                                    className="btn primary"
                                                    disabled={isReadOnly}
                                                    onClick={() => setShowSchedule((s) => !s)}
                                                >
                                                    {showSchedule
                                                        ? "Ocultar disponibilidad"
                                                        : "Ver disponibilidad"}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label htmlFor="fecha">Fecha de reserva</label>
                                        <input
                                            type="date"
                                            id="fecha"
                                            className="form-control"
                                            value={form.fecha}
                                            disabled={isReadOnly}
                                            onKeyDown={(e) => e.preventDefault()}
                                            onChange={(e) => {
                                                const v = e.target.value;

                                                // Validar que el valor no esté vacío
                                                if (!v || typeof v !== 'string' || v.trim() === '') {
                                                    console.warn('[ReservationPage] Fecha vacía o inválida rechazada');
                                                    return;
                                                }

                                                // Validar formato básico YYYY-MM-DD
                                                const datePattern = /^\d{4}-\d{2}-\d{2}$/;
                                                if (!datePattern.test(v)) {
                                                    console.warn('[ReservationPage] Formato de fecha inválido rechazado:', v);
                                                    return;
                                                }

                                                // Validar que sea una fecha válida
                                                const chosen = new Date(v + "T00:00:00");
                                                if (Number.isNaN(chosen.getTime())) {
                                                    console.warn('[ReservationPage] Fecha inválida rechazada:', v);
                                                    return;
                                                }

                                                // Validar que no sea una fecha pasada
                                                chosen.setHours(0, 0, 0, 0);
                                                if (chosen < today()) {
                                                    setAlertDialog({ open: true, title: 'Fecha Inválida', message: "No puedes reservar para fechas pasadas.", variant: 'error' });
                                                    return;
                                                }

                                                // Actualizar fecha y también fechaFin
                                                setForm((f) => ({ ...f, fecha: v, fechaFin: v }));
                                                jumpToWeekOf(chosen);
                                            }}
                                            min={formatDateLocal(today())}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="horaInicio">Hora de inicio</label>
                                        <input
                                            id="horaInicio"
                                            type="time"
                                            className="form-control"
                                            value={form.horaInicio}
                                            disabled={isReadOnly}
                                            onChange={(e) => {
                                                const v = e.target.value;

                                                // Permitir vacío (usuario borrando)
                                                if (!v || v.trim() === '') {
                                                    setForm((f) => ({ ...f, horaInicio: '' }));
                                                    return;
                                                }

                                                // Validar formato HH:mm
                                                const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
                                                if (!timePattern.test(v)) {
                                                    console.warn('[ReservationPage] Formato de hora inicio inválido rechazado:', v);
                                                    return;
                                                }

                                                // Validar que las horas y minutos sean válidos
                                                const [hours, minutes] = v.split(':').map(Number);
                                                if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
                                                    console.warn('[ReservationPage] Hora inicio fuera de rango rechazada:', v);
                                                    return;
                                                }

                                                setForm((f) => ({ ...f, horaInicio: v }));
                                            }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="fechaFin">Fecha de finalización</label>
                                        <input
                                            id="fechaFin"
                                            type="date"
                                            className="form-control"
                                            value={form.fechaFin}
                                            disabled={isReadOnly}
                                            onChange={(e) => {
                                                const v = e.target.value;

                                                // Validar que el valor no esté vacío
                                                if (!v || typeof v !== 'string' || v.trim() === '') {
                                                    console.warn('[ReservationPage] Fecha fin vacía o inválida rechazada');
                                                    return;
                                                }

                                                // Validar formato básico YYYY-MM-DD
                                                const datePattern = /^\d{4}-\d{2}-\d{2}$/;
                                                if (!datePattern.test(v)) {
                                                    console.warn('[ReservationPage] Formato de fecha fin inválido rechazado:', v);
                                                    return;
                                                }

                                                // Validar que sea una fecha válida
                                                const chosen = new Date(v + "T00:00:00");
                                                if (Number.isNaN(chosen.getTime())) {
                                                    console.warn('[ReservationPage] Fecha fin inválida rechazada:', v);
                                                    return;
                                                }

                                                setForm((f) => ({ ...f, fechaFin: v }));
                                            }}
                                            min={form.fecha}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="horaFin">Hora de finalización</label>
                                        <input
                                            id="horaFin"
                                            type="time"
                                            className="form-control"
                                            value={form.horaFin}
                                            disabled={isReadOnly}
                                            onChange={(e) => {
                                                const v = e.target.value;

                                                // Permitir vacío (usuario borrando)
                                                if (!v || v.trim() === '') {
                                                    setForm((f) => ({ ...f, horaFin: '' }));
                                                    return;
                                                }

                                                // Validar formato HH:mm
                                                const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
                                                if (!timePattern.test(v)) {
                                                    console.warn('[ReservationPage] Formato de hora fin inválido rechazado:', v);
                                                    return;
                                                }

                                                // Validar que las horas y minutos sean válidos
                                                const [hours, minutes] = v.split(':').map(Number);
                                                if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
                                                    console.warn('[ReservationPage] Hora fin fuera de rango rechazada:', v);
                                                    return;
                                                }

                                                setForm((f) => ({ ...f, horaFin: v }));
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <div className="duration">
                                            <strong>Duración de la reserva: </strong>
                                            {duracion.days > 0 && `${duracion.days} ${duracion.days === 1 ? 'día' : 'días'}, `}
                                            {duracion.h} horas {duracion.m} minutos
                                        </div>
                                        {selectedRangeLabel && (
                                            <div className="reservation-range">
                                                <strong>Horario seleccionado: </strong>
                                                {selectedRangeLabel}
                                            </div>
                                        )}
                                        <StatusBadge />
                                    </div>

                                    {/* Separador visual */}
                                    <hr style={{
                                        margin: '2rem 0',
                                        border: 'none',
                                        borderTop: '2px solid #e5e7eb',
                                        opacity: 0.6
                                    }} />

                                    {/* Trayecto y Detalles del viaje */}
                                    <div className="reservation-section__header">
                                        <h2 className="reservation-section__title">
                                            TRAYECTO Y DETALLES DEL VIAJE
                                        </h2>
                                    </div>

                                    <div className="reservation-section__body">
                                        <div className="form-group">
                                            <label htmlFor="puntoPartida">Punto de partida</label>
                                            <input
                                                id="puntoPartida"
                                                type="text"
                                                className="form-control"
                                                value={origin.address}
                                                disabled={isReadOnly}
                                                placeholder="Escribe una dirección…"
                                                onChange={(e) =>
                                                    setOrigin((o) => ({
                                                        ...o,
                                                        address: e.target.value,
                                                    }))
                                                }
                                                onFocus={() => !isReadOnly && openPicker("origin")}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !isReadOnly) {
                                                        e.preventDefault();
                                                        openPicker("origin");
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="puntoDestino">Punto de Destino</label>
                                            <input
                                                id="puntoDestino"
                                                type="text"
                                                className="form-control"
                                                value={destination.address}
                                                disabled={isReadOnly}
                                                placeholder="Escribe una dirección…"
                                                onChange={(e) =>
                                                    setDestination((o) => ({
                                                        ...o,
                                                        address: e.target.value,
                                                    }))
                                                }
                                                onFocus={() =>
                                                    !isReadOnly && openPicker("destination")
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !isReadOnly) {
                                                        e.preventDefault();
                                                        openPicker("destination");
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="motivoViaje">
                                                Motivo del Viaje <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <textarea
                                                id="motivoViaje"
                                                className="form-control"
                                                rows={3}
                                                value={form.motivo}
                                                disabled={isReadOnly}
                                                onChange={setF("motivo")}
                                                placeholder="Describe el motivo del viaje..."
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="observaciones">
                                                Observaciones (Indicaciones) Extras
                                            </label>
                                            <textarea
                                                id="observaciones"
                                                className="form-control"
                                                rows={3}
                                                value={form.observaciones}
                                                disabled={isReadOnly}
                                                onChange={setF("observaciones")}
                                                placeholder="Agrega cualquier observación o indicación adicional..."
                                            />
                                        </div>
                                    </div>

                                    {/* Botones de acción */}
                                    <div className="form-actions">
                                        {editingId && (
                                            <button
                                                type="button"
                                                className="btn primary"
                                                onClick={onDelete}
                                                disabled={isReadOnly}
                                                style={{
                                                    borderColor: "#fecaca",
                                                    color: "#b91c1c",
                                                }}
                                            >
                                                Eliminar
                                            </button>
                                        )}
                                        {editingId && (
                                            <button
                                                type="button"
                                                className="btn primary"
                                                onClick={() => resetForm()}
                                            >
                                                Cancelar
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className="btn primary"
                                            disabled={
                                                isReadOnly ||
                                                hasOverlap ||
                                                isDurationTooShort ||
                                                (editingId && !hasChanges) ||
                                                !form.idChofer ||
                                                !form.fecha ||
                                                !form.fechaFin ||
                                                !form.horaInicio ||
                                                !form.horaFin ||
                                                !origin.address ||
                                                !destination.address ||
                                                !(form.motivo || "").trim()
                                            }
                                        >
                                            {editingId ? "Actualizar" : "Reservar"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </section>

                        {/* 🔹 CARD 2: CALENDARIO + PANEL DEL CHOFER */}
                        <section className="card calendar-card">
                            {/* Top: datos chofer + horario */}
                            <div className="calendar-card__top">
                                {form.chofer && (
                                    <div className="driver-panel">
                                        <div className="dp-head">
                                            <div>
                                                <strong>Horarios de {form.chofer}</strong> —{" "}
                                                {Number.isNaN(selectedDate.getTime())
                                                    ? "Fecha inválida"
                                                    : `${weekdayLongES(selectedDate)} ${form.fecha}`
                                                }
                                            </div>
                                        </div>

                                        {intervalsForSelectedDate.length === 0 ? (
                                            <div className="dp-empty">
                                                Sin horario para este día.
                                            </div>
                                        ) : (
                                            <div className="dp-list">
                                                {intervalsForSelectedDate.map((iv, i) => (
                                                    <div key={i} className="dp-item">
                                                        <span
                                                            className="bar"
                                                            style={{ background: driverColor }}
                                                        />
                                                        <span className="label">
                                                            {iv.start} – {iv.end}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {form.horaInicio && form.horaFin && (
                                            <div
                                                className={`dp-note ${timeAnticipationIssue?.hasIssue || hasOverlap || isDurationTooShort
                                                    ? "warn"
                                                    : isInside === true && !approvalInfo.tipoId
                                                        ? "ok"
                                                        : "warn"
                                                    }`}
                                            >
                                                Seleccionado: {selectedRangeLabel || `${form.horaInicio} – ${form.horaFin}`} →{" "}
                                                {isDurationTooShort ? (
                                                    <strong>La reserva debe ser de al menos 10 minutos.</strong>
                                                ) : timeAnticipationIssue?.hasIssue ? (
                                                    <>
                                                        <strong>{timeAnticipationIssue.message}</strong>
                                                    </>
                                                ) : hasOverlap ? (
                                                    "no disponible: el chofer ya tiene una reserva en este horario."
                                                ) : isInside === true && !approvalInfo.tipoId ? (
                                                    "dentro del horario de servicio."
                                                ) : (
                                                    <>
                                                        se enviará a autorización.
                                                        {approvalInfo.label && (
                                                            <>
                                                                {" "}
                                                                <strong>{approvalInfo.label}</strong>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Bottom: toolbar + WeeklyCalendar */}
                            <div className="calendar-card__bottom">
                                <div className="calendar-toolbar">
                                    <h2>Calendario de Reservas</h2>
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <button
                                            type="button"
                                            className="btn primary"
                                            onClick={() => setShowOnlyMine((s) => !s)}
                                        >
                                            {showOnlyMine ? "Ver todas las reservas" : "Solo mis reservas"}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn primary"
                                            onClick={() => setShowRejected((s) => !s)}
                                        >
                                            {showRejected
                                                ? "Ocultar rechazadas/terminadas"
                                                : "Ver rechazadas/terminadas"}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn"
                                            onClick={() => setShowUserInfoModal(true)}
                                            style={{
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: 0,
                                                background: 'var(--color-primary)',
                                                color: '#fff',
                                                border: 'none',
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                marginLeft: 'auto'
                                            }}
                                            title="Ver información del usuario"
                                        >
                                            ℹ
                                        </button>
                                    </div>
                                </div>

                                <WeeklyCalendar
                                    anchorDate={anchor}
                                    onNavigate={(newAnchor) => setAnchor(newAnchor)}
                                    events={events}
                                    availabilityBlocks={availabilityBlocks}
                                    onEventClick={(evt) => startEdit(evt)}
                                    showSchedule={showSchedule}
                                />
                            </div>
                        </section>
                    </>
                )}
            </main>

            {/* Event Popup for Mobile */}
            <div className={`event-popup-overlay ${showEventPopup ? '' : 'hidden'}`}>
                {selectedEvent && (
                    <div className={`event-popup ${showEventPopup ? '' : 'hidden'}`}>
                        <div className="event-popup-header">
                            <h3>Detalles de la Reservación</h3>
                            <button
                                className="close-popup"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowEventPopup(false);
                                }}
                                aria-label="Cerrar"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="event-popup-content">
                            <div className="event-detail">
                                <span className="detail-label">Motivo del Viaje</span>
                                <span className="detail-value">{selectedEvent.title}</span>
                            </div>
                            <div className="event-detail">
                                <span className="detail-label">Horario</span>
                                <span className="detail-value">
                                    {format(new Date(selectedEvent.start), 'PPp', { locale: es })} - {format(new Date(selectedEvent.end), 'p', { locale: es })}
                                </span>
                            </div>
                            <div className="event-detail">
                                <span className="detail-label">Chofer</span>
                                <span className="detail-value">
                                    {selectedEvent.meta?.chofer ||
                                        selectedEvent.meta?.nombreChofer ||
                                        selectedEvent.meta?.driverName ||
                                        'No asignado'}
                                </span>
                                {selectedEvent.meta?.status && (
                                    <span className={`event-status status-${selectedEvent.meta.status.toLowerCase()}`}>
                                        {selectedEvent.meta.status}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <MapPickerGoogle
                open={pickerOpen && !hasSessionErrors}
                onClose={() => setPickerOpen(false)}
                onConfirm={confirmPicker}
                initialQuery={
                    pickerTarget === "origin" ? origin.address : destination.address
                }
            />

            {/* Modal de información del usuario */}
            <AlertDialog
                open={showUserInfoModal}
                onClose={() => setShowUserInfoModal(false)}
                title="Información del Usuario"
                variant="info"
                message={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Nombre</div>
                            <div style={{ fontWeight: 500, color: '#1e293b' }}>
                                {form.nombre || sessionUserName || "Sin nombre"}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Correo electrónico</div>
                            <div style={{ fontWeight: 500, color: '#1e293b' }}>
                                {form.email || "Sin correo"}
                            </div>
                        </div>
                    </div>
                }
            />
            {/* Dialogs Generales */}
            {alertDialog.open && (
                <AlertDialog
                    open={alertDialog.open}
                    onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
                    title={alertDialog.title}
                    message={alertDialog.message || ""}
                    variant={alertDialog.variant || "info"}
                />
            )}
            {confirmDialog.open && (
                <ConfirmDialog
                    open={confirmDialog.open}
                    onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                    title={confirmDialog.title}
                    message={confirmDialog.message || ""}
                    variant={confirmDialog.variant || "warning"}
                    onConfirm={confirmDialog.onConfirm}
                />
            )}
        </div>
    );
}
